/* eslint-disable no-param-reassign */
import { forwardRef } from 'react';
import 'tippy.js/dist/svg-arrow.css';
import { compact } from 'lodash';

import PropTypes from 'prop-types';
import { BubbleMenu } from '@tiptap/react';

import { roundArrow } from 'tippy.js';

import { TextToolsOptions } from './textToolsOptions';

import * as styled from '../styles';
import { applyBubbleBoundary, bubblePopperOptions } from '../utils';

const execSetPlacement = (editor) => (instance) => {
  const { placement } = instance.props;
  if (
    placement !== 'bottom' &&
    editor.state.selection.node?.type.name === 'variable'
  ) {
    instance.props.placement = 'bottom';
    return;
  }

  if (
    placement !== 'top' &&
    editor.state.selection.node?.type.name !== 'variable'
  ) {
    instance.props.placement = 'top';
  }
};

const ToolsFloating = forwardRef((props, ref) => {
  const {
    editor: propsEditor,
    items,
    setAskAIOpen,
    withButtonAskAI,
    withViewportBoundaries,
    onShow,
  } = props;

  const itemsWithAskAI = compact([withButtonAskAI && 'buttonAsk', ...items]);

  const handleOnShow = (instance) => {
    execSetPlacement(propsEditor);
    if (withViewportBoundaries) applyBubbleBoundary(propsEditor)(instance);
    onShow();
  };

  const viewportBoundaryOptions = withViewportBoundaries
    ? { popperOptions: bubblePopperOptions(propsEditor) }
    : {};

  return (
    <BubbleMenu
      className="tooltip-shadow"
      editor={propsEditor}
      shouldShow={({ editor: currentEditor, state, from, to }) => {
        if (currentEditor.isActive('table')) return false;
        const { empty } = state.selection;
        if (empty) return false;
        return state.doc.textBetween(from, to).length > 0;
      }}
      tippyOptions={{
        duration: 100,
        arrow: roundArrow,
        maxWidth: 'none',
        ...viewportBoundaryOptions,
        onShow: handleOnShow,
      }}
    >
      <div ref={ref}></div>
      <styled.FloatingTools>
        <TextToolsOptions
          editor={propsEditor}
          items={itemsWithAskAI}
          setAskAIOpen={setAskAIOpen}
        />
      </styled.FloatingTools>
    </BubbleMenu>
  );
});

ToolsFloating.defaultProps = {
  items: [
    'bulletList',
    'orderedList',
    'bold',
    'italic',
    'underline',
    'highlight',
  ],
};

ToolsFloating.propTypes = {
  ...TextToolsOptions.propTypes,
  onShow: PropTypes.func,
  items: PropTypes.array,
  withViewportBoundaries: PropTypes.bool,
};

function ToolsBar({ editor, items }) {
  return (
    <styled.Toolbar>
      <TextToolsOptions editor={editor} items={items} />
    </styled.Toolbar>
  );
}

ToolsBar.defaultProps = {
  items: [
    'bulletList',
    'orderedList',
    'bold',
    'italic',
    'underline',
    'highlight',
  ],
};

ToolsBar.propTypes = {
  ...TextToolsOptions.propTypes,
  items: TextToolsOptions.propTypes.items,
};

export const TextTools = forwardRef((props, ref) => {
  const {
    editor,
    items,
    type,
    setAskAIOpen,
    withButtonAskAI,
    withViewportBoundaries,
    onShow,
  } = props;
  if (!editor) return null;

  if (type === 'toolbar') {
    return <ToolsBar items={items} editor={editor} />;
  }

  return (
    <ToolsFloating
      editor={editor}
      items={items}
      setAskAIOpen={setAskAIOpen}
      withButtonAskAI={withButtonAskAI}
      withViewportBoundaries={withViewportBoundaries}
      onShow={onShow}
      ref={ref}
    />
  );
});

TextTools.defaultValues = {
  type: 'floating',
};

TextTools.propTypes = {
  ...TextToolsOptions.propTypes,
  onShow: PropTypes.func,
  withViewportBoundaries: PropTypes.bool,
  type: PropTypes.oneOf(['toolbar', 'floating']),
};
